/**
 * The bot's whole-message normaliser, mirrored (RU-106 / RU-500).
 *
 * `echoflicks-whatsapp-bot` compares what a contact typed (or which quick-reply
 * title came back) with its known phrases after
 * `bot/i18n/normalize.py::normalize_intent_text`. The CRM must fold a label
 * the same way, or its "Felix will / will not recognise this" advice is wrong.
 * `fixtures/intent_normalisation.json` is the shared contract: both repos run
 * every case in it, unchanged.
 *
 * Steps, in order (the bot's module doc):
 *
 * 1. Unicode NFKC (full-width letters, ligatures, compatibility digits);
 * 2. full case folding, as Python's `str.casefold` ("ß" -> "ss", final sigma
 *    -> sigma) — `toLowerCase` alone is not enough, see `casefold`;
 * 3. "ё" -> "е" (Russian writes both);
 * 4. every character that is not a letter, a digit or a combining mark
 *    attached to a kept character becomes a space (punctuation including "_"
 *    and apostrophes, symbols, emoji, ZWJ, variation selectors);
 * 5. whitespace runs collapse to one space, ends trimmed.
 *
 * Letters and digits of every script are kept, so Cyrillic, Greek and accented
 * Latin compare instead of collapsing to an empty string. ASCII results are the
 * same as the former `[^a-z0-9]` folding ("I bought my Echo, let's go!" ->
 * "i bought my echo let s go").
 */

const LETTER_OR_DIGIT = /^[\p{L}\p{N}]$/u;
const COMBINING_MARK = /^\p{M}$/u;

/** Cherokee is the one script whose case folding goes to UPPER case. */
function isCherokee(codePoint: number): boolean {
  return (
    (codePoint >= 0x13a0 && codePoint <= 0x13fd) ||
    (codePoint >= 0xab70 && codePoint <= 0xabbf)
  );
}

function isVariationSelector(codePoint: number): boolean {
  return (
    (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
    (codePoint >= 0xe0100 && codePoint <= 0xe01ef)
  );
}

/**
 * One character's full case folding, as Python's `str.casefold`.
 *
 * Lower, upper, lower again reaches the folded form for the characters where
 * folding is more than lowercasing: "ß"/"ẞ" -> "ss", "ς" -> "σ", Greek
 * iota-subscript letters -> "…ι", "ǰ"/"ẖ"/"ΐ" -> base + combining mark, the
 * historic Cyrillic letter variants (U+1C80…) -> their plain letter. Two
 * exceptions keep the round trip honest: Cherokee folds to upper case, and the
 * dotless "ı" folds to itself (the round trip would turn it into "i").
 * Checked against Python over every NFKC-stable code point.
 */
function casefoldChar(char: string): string {
  const codePoint = char.codePointAt(0) ?? 0;
  if (isCherokee(codePoint)) return char.toUpperCase();
  if (char === "ı") return char;
  return char.toLowerCase().toUpperCase().toLowerCase();
}

/** Python's `str.casefold`, one character at a time (no context rules). */
export function casefold(value: string): string {
  let folded = "";
  for (const char of value) folded += casefoldChar(char);
  return folded;
}

/** Casefolded letters/digits of any script, single-spaced (see module doc). */
export function normalizeIntentText(value: unknown): string {
  const text = casefold(String(value ?? "").normalize("NFKC")).replaceAll(
    "ё",
    "е",
  );
  let out = "";
  for (const char of text) {
    if (LETTER_OR_DIGIT.test(char)) {
      out += char;
    } else if (
      COMBINING_MARK.test(char) &&
      out !== "" &&
      !out.endsWith(" ") &&
      !isVariationSelector(char.codePointAt(0) ?? 0)
    ) {
      out += char;
    } else {
      out += " ";
    }
  }
  return out.split(/\s+/).filter(Boolean).join(" ");
}

/**
 * A pipe-separated catalog phrase list ("готово|всё|дальше"), each phrase
 * normalised, blanks and duplicates dropped — `split_phrases` in the bot.
 */
export function splitPhrases(value: string | null | undefined): string[] {
  const phrases = new Set<string>();
  for (const raw of String(value ?? "").split("|")) {
    const normalized = normalizeIntentText(raw);
    if (normalized) phrases.add(normalized);
  }
  return [...phrases];
}
